/** Shared transcript renderer: `appendChunk` is the incremental path, `renderChunksToMarkdown` the bulk one. */

export interface RenderableChunk {
   text: string
   speakerName: string | null
   speakerId: number
   startTime: number
}

export interface RendererState {
   markdown: string
   lastSpeakerId: number
   lastStartTime: number
}

export const initialRendererState: RendererState = {
   markdown: '',
   lastSpeakerId: Number.NaN,
   lastStartTime: Number.NEGATIVE_INFINITY,
}

export interface RenderOptions {
   /** Default true. When false, drops the "[mm:ss] " timestamp prefix. */
   includeTimestamps?: boolean
   /** Default 5000ms. A value of 0 disables the pause-based line split (same speaker
    *  consecutive chunks always concatenate). */
   pauseThresholdMs?: number
   /** Default 'outside-bold' → `**Name**: text`. 'inside-bold' → `**Name:** text`. */
   speakerColonStyle?: 'outside-bold' | 'inside-bold'
   /** Default false. When true, every non-continuation chunk is preceded by `\n\n`
    *  including the first line. Used by the frontend live UI where downstream
    *  splitters filter the leading empty token. */
   leadingSeparator?: boolean
}

const DEFAULTS = {
   includeTimestamps: true,
   pauseThresholdMs: 5000,
   speakerColonStyle: 'outside-bold' as const,
   leadingSeparator: false,
}

function formatTimestamp(ms: number): string {
   if (!Number.isFinite(ms)) return '00:00'
   const totalSeconds = Math.floor(ms / 1000)
   const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0')
   const seconds = (totalSeconds % 60).toString().padStart(2, '0')
   return `${minutes}:${seconds}`
}

function formatLine(chunk: RenderableChunk, opts: Required<RenderOptions>): string {
   const prefix = opts.includeTimestamps ? `[${formatTimestamp(chunk.startTime)}] ` : ''
   if (!chunk.speakerName) return `${prefix}${chunk.text}`
   const speaker = opts.speakerColonStyle === 'inside-bold'
      ? `**${chunk.speakerName}:**`
      : `**${chunk.speakerName}**:`
   return `${prefix}${speaker} ${chunk.text}`
}

/** Appends one chunk to the renderer state, breaking a line on a speaker change or a long pause. */
export function appendChunk(state: RendererState, chunk: RenderableChunk, options: RenderOptions = {}): RendererState {
   const opts = { ...DEFAULTS, ...options }
   const hasPause = opts.pauseThresholdMs > 0
      && Number.isFinite(state.lastStartTime)
      && (chunk.startTime - state.lastStartTime) > opts.pauseThresholdMs
   const sameSpeakerContinuation = chunk.speakerId === state.lastSpeakerId && !hasPause
   if (sameSpeakerContinuation) {
      return {
         markdown: state.markdown + ` ${chunk.text}`,
         lastSpeakerId: chunk.speakerId,
         lastStartTime: chunk.startTime,
      }
   }
   const line = formatLine(chunk, opts)
   const separator = state.markdown.length > 0 || opts.leadingSeparator ? '\n\n' : ''
   return {
      markdown: state.markdown + separator + line,
      lastSpeakerId: chunk.speakerId,
      lastStartTime: chunk.startTime,
   }
}

/** Bulk-renders chunks, sorted by startTime, into a full RendererState the next append continues from. */
export function renderChunksToState(chunks: readonly RenderableChunk[], options: RenderOptions = {}): RendererState {
   if (chunks.length === 0) return initialRendererState
   const sorted = [...chunks].sort((a, b) => a.startTime - b.startTime)
   return sorted.reduce<RendererState>((state, chunk) => appendChunk(state, chunk, options), initialRendererState)
}

/** Bulk-renders chunks and returns only the markdown string. */
export function renderChunksToMarkdown(chunks: readonly RenderableChunk[], options: RenderOptions = {}): string {
   return renderChunksToState(chunks, options).markdown
}

/** Strips the leading `[mm:ss] ` timecode from each line, with or without MDXEditor's backslash escapes. */
export function stripTranscriptTimecodes(markdown: string): string {
   return markdown.replace(/^\\?\[\d+:\d+\\?\]\s*/gm, '')
}
